import React from 'react';
import './LegalPages.css';

const EFFECTIVE_DATE = 'August 21, 2026';

function LegalShell({ title, children }) {
  return (
    <section className="legal-page">
      <header className="legal-hero">
        <div className="eyebrow">WatchVIM Legal</div>
        <h1>{title}</h1>
      </header>
      <div className="legal-card">
        <p className="legal-effective">Effective Date: {EFFECTIVE_DATE}</p>
        <div className="legal-document">{children}</div>
      </div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p>
        VIM Media, LLC (“VIM Media,” “WatchVIM,” “we,” “us,” or “our”) operates the WatchVIM streaming platform,
        website, applications, and related services (collectively, the “Services”). This Privacy Policy explains how
        we collect, use, disclose, and protect information when you use the Services.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>1.1 Information You Provide</h3>
      <ul>
        <li><strong>Account information:</strong> name, email address, account credentials, and profile details.</li>
        <li><strong>Billing and transaction information:</strong> billing details, purchase history, subscription status, and payment confirmations. Complete payment-card data is handled by our payment providers rather than stored by WatchVIM where those providers host the payment flow.</li>
        <li><strong>Support and business information:</strong> information submitted through support, filmmaker, advertising, casting, careers, or other business forms.</li>
        <li><strong>Communications:</strong> preferences and responses when you subscribe to marketing or service communications.</li>
      </ul>

      <h3>1.2 Information Collected Automatically</h3>
      <ul>
        <li><strong>Device and usage data:</strong> IP address, browser type, operating system, device identifiers, referring URLs, pages or screens viewed, and date/time information.</li>
        <li><strong>Streaming and playback data:</strong> titles viewed, watch progress, playback duration, player events, and technical errors.</li>
        <li><strong>Cookie and similar-technology data:</strong> cookies, local storage, pixels, web beacons, IP addresses, and similar identifiers used for security, preferences, analytics, advertising, and measurement.</li>
      </ul>

      <h3>1.3 Information from Third Parties</h3>
      <ul>
        <li>Payment processors and app stores may provide transaction and subscription status.</li>
        <li>Authentication, hosting, analytics, streaming, advertising, and other service providers may provide information needed to operate the Services.</li>
        <li>Business partners may provide campaign, sponsorship, distribution, or promotional information where applicable.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>Provide, operate, secure, and maintain the Services.</li>
        <li>Create and manage accounts, subscriptions, rentals, purchases, filmmaker services, and other requested features.</li>
        <li>Deliver, measure, personalize, and improve streaming and advertising experiences.</li>
        <li>Process transactions, prevent fraud, troubleshoot technical issues, and provide support.</li>
        <li>Analyze usage and performance and develop new or improved features.</li>
        <li>Communicate service, account, transactional, marketing, or legal information.</li>
        <li>Comply with applicable legal obligations and protect WatchVIM, our users, partners, and rights holders.</li>
      </ul>

      <h2>3. Cookies, Google Advertising, and Similar Technologies</h2>
      <p>
        WatchVIM and our service providers may use cookies, local storage, web beacons, pixels, IP addresses, and other
        identifiers. These technologies can support essential site functions, account sessions, security, analytics,
        advertising delivery, frequency management, fraud prevention, and measurement.
      </p>
      <p>
        Third-party vendors, including Google, may place and read cookies in your browser or use web beacons, IP
        addresses, and other identifiers as a result of ads being served on WatchVIM. Google and its partners may use
        advertising cookies to serve ads based on your visits to WatchVIM and/or other websites and apps, subject to
        applicable law, consent requirements, and your advertising choices.
      </p>
      <p>
        Learn more about how Google uses information from sites and apps that use its services at{' '}
        <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
          Google — How information from sites or apps that use our services is used
        </a>.
      </p>
      <p>
        You can manage personalized advertising choices through{' '}
        <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.
        You may also be able to opt out of certain participating companies’ interest-based advertising through{' '}
        <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">YourAdChoices</a>.
      </p>
      <p>
        Where other third-party advertising vendors or ad networks are enabled, they may also use cookies or similar
        technologies for advertising and measurement. WatchVIM’s root <a href="/ads.txt">ads.txt</a> file identifies
        entities authorized to sell or resell applicable WatchVIM advertising inventory; individual vendors may provide
        their own privacy and opt-out controls.
      </p>
      <p>
        See our <a href="/cookie-policy">Cookie Policy</a> for additional information about cookie categories and your
        browser or device controls.
      </p>

      <h2>4. Personalized and Non-Personalized Advertising</h2>
      <p>
        Depending on your region, consent status, device settings, advertising preferences, and the advertising service
        being used, WatchVIM may request personalized, non-personalized, or limited advertising. Personalized advertising
        can use information such as prior visits or interests. Non-personalized advertising may use contextual information
        such as the content being viewed, general location, or limited technical information while still using certain
        technologies for delivery, frequency capping, fraud prevention, and reporting where permitted.
      </p>

      <h2>5. How We Share Information</h2>
      <p>We do not sell personal information in exchange for money. We may disclose information to:</p>
      <ul>
        <li><strong>Service providers:</strong> hosting, streaming, analytics, authentication, payments, email, support, advertising, fraud prevention, and similar vendors that help operate WatchVIM.</li>
        <li><strong>Advertising and measurement partners:</strong> as needed to request, deliver, measure, secure, and report advertising, subject to applicable law and consent requirements.</li>
        <li><strong>Business partners:</strong> for distribution, co-branded, sponsored, or promotional activities where appropriate.</li>
        <li><strong>Legal and safety recipients:</strong> where required by law or reasonably necessary to protect rights, safety, users, or the Services.</li>
        <li><strong>Business transfers:</strong> in connection with a merger, financing, acquisition, restructuring, or transfer of assets.</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>
        We retain information for as long as reasonably necessary for the purposes described in this policy, including
        providing the Services, maintaining records, preventing fraud, resolving disputes, enforcing agreements, and
        satisfying legal obligations. Retention periods may vary by data type and purpose.
      </p>

      <h2>7. Your Choices and Rights</h2>
      <p>
        Depending on where you live, you may have rights regarding access, correction, deletion, portability, objection,
        restriction, or certain uses of personal information. You can also use available browser, device, advertising,
        and consent controls. For privacy requests, contact <a href="mailto:streaming@watchvim.com">streaming@watchvim.com</a>.
      </p>

      <h2>8. Security</h2>
      <p>
        We use reasonable administrative, technical, and organizational safeguards designed to protect information.
        No transmission or storage method is completely secure, so we cannot guarantee absolute security.
      </p>

      <h2>9. Children’s Privacy</h2>
      <p>
        WatchVIM accounts and purchases are not intended to be created by children under 13, or a higher minimum age
        where required by applicable law. If you believe a child has provided personal information to us improperly,
        contact us so we can review the matter.
      </p>

      <h2>10. International Users and Consent</h2>
      <p>
        Information may be processed in the United States and other countries where WatchVIM or our providers operate.
        Privacy, advertising, and consent requirements vary by jurisdiction. Where required, advertising or similar
        non-essential technologies may be subject to consent or other regional controls before use.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy as our Services, vendors, legal obligations, or privacy practices change. We will
        revise the effective date shown above when we publish an updated version.
      </p>

      <h2>12. Contact Us</h2>
      <p><strong>VIM Media, LLC</strong><br />1801 E Camelback Rd Ste 102-1305<br />Phoenix, AZ 85016</p>
      <p>Email: <a href="mailto:streaming@watchvim.com">streaming@watchvim.com</a></p>
    </LegalShell>
  );
}

export function CookiePolicyPage() {
  return (
    <LegalShell title="Cookie Policy">
      <p>
        This Cookie Policy explains how WatchVIM uses cookies and similar technologies on our website and web-based
        streaming experience. It should be read together with our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>1. What Cookies and Similar Technologies Are</h2>
      <p>
        Cookies are small data files stored by a browser. WatchVIM and service providers may also use local storage,
        pixels, web beacons, software development tools, IP addresses, and similar identifiers to operate, secure,
        understand, and monetize the Services.
      </p>

      <h2>2. How WatchVIM Uses These Technologies</h2>
      <h3>Essential</h3>
      <p>Used for security, authentication, account sessions, fraud prevention, load balancing, and core site functionality.</p>
      <h3>Preferences and Functionality</h3>
      <p>Used to remember settings and improve continuity across the WatchVIM experience where enabled.</p>
      <h3>Analytics and Performance</h3>
      <p>Used to understand site, streaming, and player performance, diagnose errors, measure engagement, and improve the Services.</p>
      <h3>Advertising and Measurement</h3>
      <p>
        Used, where permitted, to request, deliver, measure, limit frequency, prevent invalid traffic, and report ads.
        Third-party vendors, including Google, may use cookies, web beacons, IP addresses, or other identifiers in
        connection with advertising served on WatchVIM.
      </p>

      <h2>3. Google Advertising Cookies</h2>
      <p>
        Google uses cookies to help serve ads on partner sites. Google’s use of advertising cookies may allow Google and
        its partners to serve ads based on visits to WatchVIM and/or other sites or apps, depending on applicable law,
        consent, and your settings.
      </p>
      <p>
        Learn more at{' '}
        <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">Google’s partner-sites privacy information</a>{' '}
        and manage personalized ad choices at{' '}
        <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.
      </p>

      <h2>4. Third-Party Advertising Vendors</h2>
      <p>
        Other advertising vendors or networks may be used from time to time. Those providers may maintain their own
        privacy policies and opt-out mechanisms. WatchVIM’s <a href="/ads.txt">ads.txt</a> file identifies entities
        authorized to sell or resell applicable WatchVIM advertising inventory.
      </p>

      <h2>5. Your Choices</h2>
      <p>
        You can control cookies through browser or device settings. Blocking essential storage may prevent account,
        playback, checkout, or other site features from working correctly. Where WatchVIM presents a consent-management
        interface, you can use that interface to make available choices for non-essential technologies.
      </p>
      <p>
        For additional industry opt-out choices, visit{' '}
        <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">YourAdChoices</a>.
      </p>

      <h2>6. Changes to This Cookie Policy</h2>
      <p>
        We may update this policy when our technologies, advertising partners, Services, or legal requirements change.
        The effective date above identifies the current published version.
      </p>

      <h2>7. Contact</h2>
      <p>Questions about this Cookie Policy can be sent to <a href="mailto:streaming@watchvim.com">streaming@watchvim.com</a>.</p>
    </LegalShell>
  );
}
